import type { ProjectFormSchemaInterface } from '../projectFormSchema';

export type FieldMetadata = {
  label: string;
  description: string;
  aiHint: string;
};

// The `satisfies` clause is the AI-native exhaustiveness gate: adding a key to
// ProjectFormSchemaInterface without adding metadata here fails compilation.
// Labels and descriptions are i18n keys; aiHint is free-form English copy that
// future AI surfaces (voice prefill, email extraction, sidebar drawer) consume
// directly without further translation.
export const projectFieldMetadata = {
  id: {
    label: 'project.form.id',
    description: 'project.form.id.description',
    aiHint:
      'Internal Tread project identifier. AI agents should not set this — it is assigned by the BE.',
  },
  name: {
    label: 'project.form.name',
    description: 'project.form.name.description',
    aiHint: 'Free-text project name; usually city or jobsite reference.',
  },
  company: {
    label: 'project.form.company',
    description: 'project.form.company.description',
    aiHint:
      'The Tread company this project belongs to. Match against the current user company unless explicitly directed.',
  },
  account: {
    label: 'project.form.account',
    description: 'project.form.account.description',
    aiHint:
      'The customer account this project is for. Match against existing accounts by name.',
  },
  department: {
    label: 'project.form.department',
    description: 'project.form.department.description',
    aiHint: 'Internal department owning this project; depends on the selected company.',
  },
  externalId: {
    label: 'project.form.external_id',
    description: 'project.form.external_id.description',
    aiHint: 'Customer-side or ERP-side identifier for this project. Free-text.',
  },
  contract_number: {
    label: 'project.form.contract_number',
    description: 'project.form.contract_number.description',
    aiHint:
      'Contract reference number from the customer. Visible only when the show_contract_number company setting is on.',
  },
  dispatch_number: {
    label: 'project.form.dispatch_number',
    description: 'project.form.dispatch_number.description',
    aiHint: 'Dispatch reference label shown to drivers and on tickets.',
  },
  poJobNumber: {
    label: 'project.form.po_job_number',
    description: 'project.form.po_job_number.description',
    aiHint: 'Customer purchase-order or job number associated with the project.',
  },
  phases: {
    label: 'project.form.phases',
    description: 'project.form.phases.description',
    aiHint:
      'Ordered list of project phases (name + code). Edit via the PhaseDrawer; AI agents can append phases by id+name.',
  },
  state: {
    label: 'project.form.state',
    description: 'project.form.state.description',
    aiHint: 'Lifecycle state (e.g. active, archived). Server-managed; do not set.',
  },
  effectiveAt: {
    label: 'project.form.effective_at',
    description: 'project.form.effective_at.description',
    aiHint:
      'ISO datetime when the project becomes effective. Round-trips to BE field effective_at.',
  },
  expiresAt: {
    label: 'project.form.expires_at',
    description: 'project.form.expires_at.description',
    aiHint:
      'ISO datetime when the project expires. Must be after effectiveAt. Round-trips to BE field expires_at.',
  },
  notes: {
    label: 'project.form.notes',
    description: 'project.form.notes.description',
    aiHint:
      'Legacy notes blob (kept for back-compat). Prefer projectNotes / orderNotes / jobNotes / internalNotes.',
  },
  projectNotes: {
    label: 'project.form.project_notes',
    description: 'project.form.project_notes.description',
    aiHint: 'Free-form notes visible at the project level.',
  },
  orderNotes: {
    label: 'project.form.order_notes',
    description: 'project.form.order_notes.description',
    aiHint:
      'Default notes copied into orders created under this project. Edit-time only.',
  },
  jobNotes: {
    label: 'project.form.job_notes',
    description: 'project.form.job_notes.description',
    aiHint: 'Default notes copied into jobs created under this project. Edit-time only.',
  },
  internalNotes: {
    label: 'project.form.internal_notes',
    description: 'project.form.internal_notes.description',
    aiHint: 'Notes visible only to the owning company; not shared with vendors.',
  },
  projectTerms: {
    label: 'project.form.quote_pdf_terms',
    description: 'project.form.quote_pdf_terms.description',
    aiHint:
      'Quote PDF terms for this project. Overrides the company-wide default Quote PDF terms; leave blank to use the default.',
  },
  site: {
    label: 'project.form.site',
    description: 'project.form.site.description',
    aiHint:
      'Primary site associated with the project; used for legacy address rendering.',
  },
  pickUpWayPoint: {
    label: 'project.form.pick_up_way_point',
    description: 'project.form.pick_up_way_point.description',
    aiHint: 'Pickup waypoint (site + optional contact + ordinality).',
  },
  dropOffWayPoint: {
    label: 'project.form.drop_off_way_point',
    description: 'project.form.drop_off_way_point.description',
    aiHint: 'Drop-off waypoint (site + optional contact + ordinality).',
  },
  stagingWayPoint: {
    label: 'project.form.staging_way_point',
    description: 'project.form.staging_way_point.description',
    aiHint: 'Optional intermediate staging waypoint. Gated by the staging_waypoint FF.',
  },
  projectMaterialTotals: {
    label: 'form_fields.materials',
    description: 'project.form.project_material_totals.description',
    aiHint:
      'Array of project-level material totals: material + unitOfMeasure + totalQuantity + optional phase + pickup site. The site is required on each line when the fastWeighEnabled flag is on.',
  },
  materialRates: {
    label: 'project.form.material_rates',
    description: 'project.form.material_rates.description',
    aiHint:
      'Per-material rate rows used by the FreightRates panel. Each row carries material + unitOfMeasure + materialRateSite + buy/sell prices.',
  },
  allContacts: {
    label: 'project.form.contacts',
    description: 'project.form.contacts.description',
    aiHint:
      'List of all contacts on the project (name + phone + type). The Contacts panel manages this collection.',
  },
  foremen: {
    label: 'project.form.foremen',
    description: 'project.form.foremen.description',
    aiHint: 'User ids of foremen assigned to this project.',
  },
  collaborators: {
    label: 'project.form.collaborators',
    description: 'project.form.collaborators.description',
    aiHint: 'User ids of internal collaborators on this project.',
  },
  salespeople: {
    label: 'project.form.salespeople',
    description: 'project.form.salespeople.description',
    aiHint: 'User ids of salespeople attached to this project.',
  },
  requester: {
    label: 'project.form.requester',
    description: 'project.form.requester.description',
    aiHint:
      'Single user from the selected company who is the project requester. Bound to companyId for typeahead.',
  },
  fileAttachments: {
    label: 'project.form.file_attachments',
    description: 'project.form.file_attachments.description',
    aiHint:
      'Uploaded file attachments scoped to this project. Managed via the FileAttachments panel.',
  },
  haulerRateType: {
    label: 'project.form.hauler_rate_type',
    description: 'project.form.hauler_rate_type.description',
    aiHint:
      'Rate type label/value pair (e.g. Per Load / rate_per_load). Required when tomlinsonOrderModal FF is on.',
  },
  loadUnitOfMeasure: {
    label: 'project.form.load_unit_of_measure',
    description: 'project.form.load_unit_of_measure.description',
    aiHint:
      'Unit-of-measure used when billing per load. Optional unless tied to a load-based haulerRateType.',
  },
  recurringLoads: {
    label: 'project.form.recurring_loads',
    description: 'project.form.recurring_loads.description',
    aiHint: 'Toggle indicating the project allows recurring/running loads.',
  },
  chaseMissingTickets: {
    label: 'project.form.chase_missing_tickets',
    description: 'project.form.chase_missing_tickets.description',
    aiHint:
      'Toggle gated by driverAgent FF. Default value seeded from the chase_missing_tickets_default_on company setting at form mount.',
  },
  liveEta: {
    label: 'project.form.enable_live_eta',
    description: 'project.form.enable_live_eta.description',
    aiHint:
      'Toggle gated by the liveEtaEnabled FF: opt this project into Dispatch Live ETA. New projects default from the live_eta_default company setting. Changing it overwrites the setting on all incomplete orders and their active jobs (BE-authoritative cascade).',
  },
  defaultDigitalTicketView: {
    label: 'project.form.default_digital_ticket_view',
    description: 'project.form.default_digital_ticket_view.description',
    aiHint:
      'Toggle gated by the show_default_digital_ticket_view company setting. Controls the default ticket-view mode for jobs under this project.',
  },
  prevailingWage: {
    label: 'project.form.prevailing_wage',
    description: 'project.form.prevailing_wage.description',
    aiHint: 'Toggle indicating the project is subject to prevailing-wage requirements.',
  },
  applyRateChangesToExistingOrders: {
    label: 'project.form.apply_rate_changes_to_existing_orders',
    description: 'project.form.apply_rate_changes_to_existing_orders.description',
    aiHint:
      'When on, rate changes can cascade to existing orders (gated by the selective-apply impact dialog at submit); when off, changes only affect orders created afterward.',
  },
  vendorPayCommissionPercentage: {
    label: 'pay_commission.percentage_label',
    description: 'pay_commission.share_hint',
    aiHint:
      'Commission deducted from gross vendor pay; zero suppresses inheritance and null restores it.',
  },
  driverPayCommissionPercentage: {
    label: 'pay_commission.percentage_label',
    description: 'pay_commission.share_hint',
    aiHint:
      'Commission deducted from gross driver pay; zero suppresses inheritance and null restores it.',
  },
  materialRateItemized: {
    label: 'form_fields.itemize_material_rate',
    description: 'project.form.material_rate_itemized.description',
    aiHint:
      'Toggle: itemize the material rate on invoices for orders under this project.',
  },
  chargeToAccount: {
    label: 'project.form.charge_to_account',
    description: 'project.form.charge_to_account.description',
    aiHint:
      'Optional alternate billing account ({ id, name }). Hidden when tomlinsonOrderModal FF is on.',
  },
  serviceClass: {
    label: 'project.form.service_class',
    description: 'project.form.service_class.description',
    aiHint:
      'Service classification ({ id, name }). Visible only when the serviceClass FF is on.',
  },
  jobTime: {
    label: 'project.form.job_time',
    description: 'project.form.job_time.description',
    aiHint:
      'Default job duration in hours. Stored as a number; UI may divide BE minutes by 60.',
  },
  orderQuantity: {
    label: 'project.form.order_quantity',
    description: 'project.form.order_quantity.description',
    aiHint: 'Default order quantity used to pre-populate orders under this project.',
  },
  orderUnitOfMeasure: {
    label: 'project.form.order_unit_of_measure',
    description: 'project.form.order_unit_of_measure.description',
    aiHint:
      'Unit-of-measure paired with orderQuantity ({ id, name }). Required when orderQuantity is set.',
  },
  truckCount: {
    label: 'project.form.truck_count',
    description: 'project.form.truck_count.description',
    aiHint: 'Default truck count to pre-populate orders under this project.',
  },
  unitsPerHour: {
    label: 'project.form.units_per_hour',
    description: 'project.form.units_per_hour.description',
    aiHint: 'Default throughput (units per hour) for orders under this project.',
  },
  equipmentType: {
    label: 'project.form.equipment_type',
    description: 'project.form.equipment_type.description',
    aiHint: 'Default equipment type ({ id, name }) for orders under this project.',
  },
  equipmentTypeGrossCapacity: {
    label: 'project.form.equipment_type_gross_capacity',
    description: 'project.form.equipment_type_gross_capacity.description',
    aiHint:
      'Default gross capacity (number) for the chosen equipmentType. Number-string accepted.',
  },
  equipmentTypeGrossCapacityUnitOfMeasure: {
    label: 'project.form.equipment_type_gross_capacity_unit_of_measure',
    description: 'project.form.equipment_type_gross_capacity_unit_of_measure.description',
    aiHint: 'Unit-of-measure paired with the gross-capacity field ({ id, name }).',
  },
  jobQuantity: {
    label: 'form_fields.job_quantity',
    description: 'project.form.job_quantity.description',
    aiHint:
      'Default job quantity. Required unless jobUnitOfMeasure is Load (cross-field rule).',
  },
  jobUnitOfMeasure: {
    label: 'project.form.job_unit_of_measure',
    description: 'project.form.job_unit_of_measure.description',
    aiHint:
      'Unit-of-measure for jobQuantity ({ id, name }). When id === "Load", jobQuantity becomes optional.',
  },
  taxRateSchedule: {
    label: 'project.form.tax_rate_schedule',
    description: 'project.form.tax_rate_schedule.description',
    aiHint:
      'Tax-rate-schedule reference ({ label, value }). Setting this auto-populates taxRate via context effect. Required when the fastWeighEnabled flag is on.',
  },
  taxRate: {
    label: 'project.form.tax_rate',
    description: 'project.form.tax_rate.description',
    aiHint:
      'Effective tax rate (0..100). Auto-populated from taxRateSchedule._entity.rate when a schedule is selected.',
  },
  taxFreight: {
    label: 'project.form.tax_freight',
    description: 'project.form.tax_freight.description',
    aiHint: 'Toggle: apply the tax rate to freight charges.',
  },
  taxMaterial: {
    label: 'project.form.tax_material',
    description: 'project.form.tax_material.description',
    aiHint: 'Toggle: apply the tax rate to material charges.',
  },
  taxAddOns: {
    label: 'project.form.tax_add_ons',
    description: 'project.form.tax_add_ons.description',
    aiHint: 'Toggle: apply the tax rate to add-on charges.',
  },
  customerRate: {
    label: 'freight_rates.customer_rate',
    description: 'freight_rates.customer_rate.description',
    aiHint:
      'Customer-side rate (number | null). Written into form state by the FreightRates widget — not part of the yup schema.',
  },
  customerRateType: {
    label: 'freight_rates.customer_rate_type',
    description: 'freight_rates.customer_rate_type.description',
    aiHint:
      'Customer-side rate type (e.g. rate_per_ton). Set by FreightRates side channel.',
  },
  customerRateValue: {
    label: 'freight_rates.customer_rate_value',
    description: 'freight_rates.customer_rate_value.description',
    aiHint: 'Customer-side rate value. Set by FreightRates side channel.',
  },
  vendorRate: {
    label: 'freight_rates.vendor_rate',
    description: 'freight_rates.vendor_rate.description',
    aiHint: 'Vendor-side rate (number | null). Set by FreightRates side channel.',
  },
  vendorRateType: {
    label: 'freight_rates.vendor_rate_type',
    description: 'freight_rates.vendor_rate_type.description',
    aiHint: 'Vendor-side rate type. Set by FreightRates side channel.',
  },
  vendorRateValue: {
    label: 'freight_rates.vendor_rate_value',
    description: 'freight_rates.vendor_rate_value.description',
    aiHint: 'Vendor-side rate value. Set by FreightRates side channel.',
  },
  driverRate: {
    label: 'freight_rates.driver_rate',
    description: 'freight_rates.driver_rate.description',
    aiHint: 'Driver-side rate (number | null). Set by FreightRates side channel.',
  },
  driverRateType: {
    label: 'freight_rates.driver_rate_type',
    description: 'freight_rates.driver_rate_type.description',
    aiHint: 'Driver-side rate type. Set by FreightRates side channel.',
  },
  driverRateValue: {
    label: 'freight_rates.driver_rate_value',
    description: 'freight_rates.driver_rate_value.description',
    aiHint: 'Driver-side rate value. Set by FreightRates side channel.',
  },
} as const satisfies Record<keyof ProjectFormSchemaInterface, FieldMetadata>;
